/**
 * Matching what somebody is typing against what other people already typed.
 *
 * The point is one spelling per dish. Two guests writing "Biryani" and
 * "biryani" is a nuisance the fold in `domain/asks.py` already handles; two
 * guests writing "biryani" and "briyani" is the same dish counted twice, and
 * no amount of case folding finds it. A plain substring filter — which is
 * what a combo box does by default — will never offer the first to somebody
 * typing the second, because the letters are in the wrong order.
 *
 * So: bigram similarity, which is exactly the trick `pg_trgm` uses on the
 * server side. "biryani" and "briyani" share three of six letter pairs and
 * score 0.5, while "biryani" and "pizza" share none.
 *
 * Deliberately only a SUGGESTION. Nothing here rewrites what a person typed:
 * they see a close match and pick it, or they ignore it and keep their own
 * spelling, which may well be the correct one.
 */

/** Overlapping letter pairs of a folded string. */
function bigrams(value: string): string[] {
  const folded = value.toLowerCase().replace(/\s+/g, ' ').trim()
  const pairs: string[] = []
  for (let i = 0; i < folded.length - 1; i++) pairs.push(folded.slice(i, i + 2))
  return pairs
}

/**
 * Dice coefficient: 1.0 identical, 0.0 nothing in common.
 *
 * Counts each pair once per occurrence rather than as a set, so "aaa" and
 * "aaaaaa" are not treated as the same word.
 */
export function similarity(a: string, b: string): number {
  const left = bigrams(a)
  const right = bigrams(b)
  if (!left.length || !right.length) return a === b ? 1 : 0

  const pool = [...right]
  let shared = 0
  for (const pair of left) {
    const at = pool.indexOf(pair)
    if (at !== -1) {
      shared++
      pool.splice(at, 1)
    }
  }
  return (2 * shared) / (left.length + right.length)
}

//: Below this two words are different things. Tuned on the case that started
//: this — "briyani" against "biryani" scores 0.5 — and kept high enough that
//: a short word does not drag in every other short word.
const NEAR = 0.45

/**
 * The options worth offering for what has been typed so far.
 *
 * Substring first, because somebody typing "bir" means it and wants the
 * ordinary behaviour. Near-misses come after, so a typo is rescued without
 * pushing the exact match down the list.
 */
export function matches(options: string[], typed: string): string[] {
  const query = typed.toLowerCase().trim()
  if (!query) return options

  const contains: string[] = []
  const near: string[] = []
  for (const option of options) {
    if (option.toLowerCase().includes(query)) contains.push(option)
    else if (similarity(option, query) >= NEAR) near.push(option)
  }
  return [...contains, ...near]
}
